import { Box, Divider, IconButton, Menu, MenuItem } from '@mui/material';
import type { PlanNode } from '@timelog/pm-shared';
import { useState } from 'react';
import type { MouseEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { areResourcesEditable, isHoursEditable } from './cells/editFields';
import { InlineDateCell } from './cells/InlineDateCell';
import { InlineTextCell } from './cells/InlineTextCell';
import type { CommitIntent } from './cells/InlineTextCell';
import { useFieldEditing } from './cells/useFieldEditing';
import { DaysLeftLabel } from './DaysLeftLabel';
import type { FlatRow } from './editor';
import { createPlanNode, usePlanEditor } from './editor';
import { KeyboardHintBar } from './KeyboardHintBar';
import { displayPlanCode } from './planCode';
import { usePlanView } from './PlanViewContext';
import { ResourcesPopover } from './ResourcesPopover';
import { RowContractChip } from './RowContractChip';
import { defaultScheduleOf } from './timeline/timelineModel';
import { COLUMN_WIDTH, INDENT_PX } from './treeGeometry';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { formatHours, formatShortDate, isBoolean, todayIso, usePersistedState } from '@/lib';
import { roleColor } from '@/lib/roleColor';

/**
 * A row's inline icon control.
 *
 * This used to be a local component *named* `IconButton`, which shadowed MUI's
 * inside this file — so `IconButton` here and `IconButton` anywhere else in the
 * app were different components with different sizing and no shared focus
 * behaviour. It is now a thin wrapper over the real one: the theme sizes
 * `size="tiny"` from `--input-height-tiny` (24px), and the focus ring and
 * ripple-suppression come from `MuiButtonBase`.
 */
const RowIconButton = ({
  icon,
  title,
  width,
  iconSize = 18,
  iconColor,
  onClick,
  automationId,
}: {
  icon: string;
  title?: string;
  /** Narrows the 24px `tiny` control where the row needs the horizontal space. */
  width?: number;
  iconSize?: number;
  iconColor?: string;
  onClick?: (event: MouseEvent) => void;
  automationId?: string;
}) => (
  <IconButton
    size="tiny"
    title={title}
    aria-label={title}
    data-automation-id={automationId}
    onClick={onClick}
    sx={{
      flex: 'none',
      ...(width === undefined ? {} : { width }),
      borderRadius: 'var(--border-radius-small)',
      color: 'var(--fg-3)',
      '&:hover': { bgcolor: 'var(--bg-hover)', color: 'var(--fg-1)' },
    }}
  >
    <MaterialSymbol name={icon} size={iconSize} color={iconColor} />
  </IconButton>
);

/**
 * The resources line under the title: who is on this task, or the way to put
 * someone on it. Either way clicking it opens the Resources popover — the same
 * editor the kebab's "Add resources" opens.
 *
 * v8 kept unstaffed rows compact and let this line exist only once allocations
 * did, on the grounds that an extra line would change row heights and shift the
 * drag zones. The first pilot test priced that: with no line, the kebab was the
 * only way to staff a task and testers did not find it.
 *
 * The drag-zone half of the objection does not hold. `zoneInRow` is
 * proportional with a 9px floor and reads the row's *measured* height, and the
 * virtualiser measures rather than trusts `ROW_ESTIMATE_PX`. Heights also get
 * MORE uniform, not less: before, parents and unstaffed leaves were short while
 * staffed leaves were tall; now every staffable row carries the line.
 *
 * The density half does hold — this costs 17px per leaf row (55 → 72), which is
 * why the kebab keeps its own "Add resources" item. If a compact density mode
 * ever takes this line away, that item is the affordance that survives it.
 */
const RowResourcesCell = ({
  node,
  canEdit,
  onOpen,
}: {
  node: PlanNode;
  canEdit: boolean;
  onOpen: (event: MouseEvent<HTMLElement>) => void;
}) => {
  const { t } = useTranslation(['pmPlanning', 'projectManagement']);
  // A row that cannot hold allocations at all (a parent, BR-1) stays compact:
  // there is nothing to show and nothing to offer.
  if (node.allocations.length === 0 && !canEdit) return null;

  const staffed = node.allocations.length > 0;
  const shown = node.allocations.slice(0, 4);
  const more = node.allocations.length - shown.length;

  /*
    ONE button in both states, with only its contents switching. It is the
    popover's anchor, and a button that unmounted when the first allocation
    landed would detach the anchor mid-edit and drop the popover to the corner.
  */
  return (
    <Box
      component="button"
      type="button"
      title={
        staffed
          ? canEdit
            ? t('row.EditResources')
            : undefined
          : t('projectManagement:projectPlan.actions.AddResources')
      }
      data-automation-id="plan-row-resources-summary"
      data-state={staffed ? 'staffed' : 'empty'}
      onClick={canEdit ? onOpen : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 0.5,
        p: 0,
        border: 'none',
        bgcolor: 'transparent',
        fontFamily: 'var(--font-family)',
        cursor: canEdit ? 'pointer' : 'default',
        '&:hover .plan-row-add-resource': {
          borderColor: 'var(--color-pink-80)',
          color: 'var(--color-pink-80)',
        },
      }}
    >
      {staffed ? (
        <Box sx={{ display: 'flex', alignItems: 'center', pl: 0.5 }}>
          {shown.map((allocation) => {
            const person = allocation.personRef?.snapshot.name;
            const role = allocation.roleRef.snapshot.name;
            return (
              <Box
                key={allocation.id}
                component="span"
                title={t('row.AllocationTitle', {
                  name: person ?? role,
                  hours: formatHours(allocation.hours),
                })}
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  bgcolor: roleColor(allocation.roleRef.tlpId),
                  color: 'var(--color-black-00)',
                  fontSize: 10,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'var(--border-width-thick) solid var(--bg-panel)',
                  ml: -0.5,
                }}
              >
                {(person ?? role).charAt(0)}
              </Box>
            );
          })}
        </Box>
      ) : (
        /*
          The timeline's `tl-add` circle, at the LIST's avatar size (20, not
          18): it sits exactly where the first avatar appears once the task is
          staffed, and a circle that changed size on the way would make the
          line jump.
        */
        <Box
          component="span"
          className="plan-row-add-resource"
          sx={{
            width: 20,
            height: 20,
            flex: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'var(--border-width-thin) dashed var(--border-subtle)',
            borderRadius: '50%',
            bgcolor: 'var(--bg-panel)',
            color: 'var(--fg-3)',
            transition: 'color var(--duration-instant), border-color var(--duration-instant)',
          }}
        >
          <MaterialSymbol name="add" size={13} />
        </Box>
      )}
      <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-3)' }}>
        {staffed
          ? `${t('projectManagement:projectPlan.resources.ResourceCount', { count: node.allocations.length })}${more > 0 ? t('row.MoreResources', { count: more }) : ''}`
          : t('projectManagement:projectPlan.actions.AddResources')}
      </Box>
    </Box>
  );
};

/**
 * Row actions (v5, decision 10): explicit affordances instead of the removed
 * hover insert-between and the inline card's footer. "Add subtask" auto-unfolds
 * a folded parent so the new row is never created out of sight.
 *
 * The kebab is always visible, on every row — it used to reveal on row hover,
 * which hid the only entry point to Add subtask / Add resources / Duplicate
 * until you happened to be over the right row (and hid it outright on touch).
 * The drag handle beside it keeps the hover reveal: it is a direct
 * manipulation affordance, not a menu, and X-drags are the one thing a row has
 * no other way to start.
 */
const RowKebab = ({
  row,
  canEditResources,
  onOpenResources,
}: {
  row: FlatRow;
  canEditResources: boolean;
  /** The kebab's own button, so the popover opens beside what was clicked. */
  onOpenResources: (anchorEl: HTMLElement) => void;
}) => {
  const { t } = useTranslation(['pmPlanning', 'projectManagement']);
  const { state, dispatch } = usePlanEditor();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const node = row.node;

  const addSubtask = () => {
    setAnchor(null);
    if (state.foldedIds.has(node.id)) dispatch({ type: 'TOGGLE_FOLD', id: node.id });
    dispatch({
      type: 'INSERT_NODE',
      node: createPlanNode('task', t('projectManagement:projectPlan.NewTaskName')),
      position: { parentId: node.id, childIndex: Number.MAX_SAFE_INTEGER },
      asChildOf: node.id,
      focusTitle: true,
    });
  };

  return (
    <>
      <RowIconButton
        icon="more_vert"
        title={t('row.RowActions')}
        automationId="plan-row-kebab"
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(event.currentTarget as HTMLElement);
        }}
      />
      <Menu
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 168 } } }}
      >
        <MenuItem data-automation-id="kebab-add-subtask" onClick={addSubtask}>
          <MaterialSymbol name="subdirectory_arrow_right" size={16} color="var(--fg-3)" />
          <Box component="span" sx={{ ml: 1 }}>
            {t('projectManagement:projectPlan.actions.AddSubtask')}
          </Box>
        </MenuItem>
        {canEditResources ? <Divider /> : null}
        {canEditResources ? (
          <MenuItem
            data-automation-id="kebab-edit-resources"
            onClick={() => {
              // Anchor to the kebab button, not this MenuItem: the menu
              // unmounts on close, taking the anchor with it.
              if (anchor) onOpenResources(anchor);
              setAnchor(null);
              dispatch({ type: 'OPEN_RESOURCES', id: node.id });
            }}
          >
            <MaterialSymbol name="group_add" size={16} color="var(--color-purple-80)" />
            <Box component="span" sx={{ ml: 1 }}>
              {t('projectManagement:projectPlan.actions.AddResources')}
            </Box>
          </MenuItem>
        ) : null}
        <Divider />
        <MenuItem
          data-automation-id="kebab-duplicate"
          onClick={() => {
            setAnchor(null);
            dispatch({
              type: 'DUPLICATE_NODES',
              ids: new Set([node.id]),
              makeId: () => crypto.randomUUID(),
            });
          }}
        >
          <MaterialSymbol name="content_copy" size={16} color="var(--fg-3)" />
          <Box component="span" sx={{ ml: 1 }}>
            {t('projectManagement:projectPlan.actions.DuplicateTask')}
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem
          data-automation-id="kebab-delete"
          onClick={() => {
            setAnchor(null);
            dispatch({ type: 'DELETE_NODES', ids: new Set([node.id]) });
          }}
          sx={{
            color: 'var(--color-coral-80)',
            '&:hover': { bgcolor: 'var(--color-coral-10)' },
          }}
        >
          <MaterialSymbol name="delete" size={16} color="var(--color-coral-80)" />
          <Box component="span" sx={{ ml: 1 }}>
            {t('projectManagement:projectPlan.actions.DeleteTask')}
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
};

export type PlanRowProps = {
  row: FlatRow;
  dragging?: boolean;
  /** A drop right now would land INSIDE this row (the drag's middle zone). */
  dropInto?: boolean;
  onDragHandleDown?: (event: ReactPointerEvent) => void;
};

export const PlanRow = ({
  row,
  dragging = false,
  dropInto = false,
  onDragHandleDown,
}: PlanRowProps) => {
  const { t } = useTranslation(['pmPlanning', 'pmCommon', 'projectManagement']);
  const { state, dispatch, flat, rollup } = usePlanEditor();
  const { commitField, isEditing, shouldOpenPicker, startEditing, stopEditing } = useFieldEditing();
  /*
    A task with no dates is drawn dashed in the timeline, today → target. A
    start typed on it writes that suggested end too, or the bar would vanish
    (a start with no end draws nothing); typing the suggested start itself
    writes nothing. The rule the side panel's Start – end line had.
  */
  const commitStart = (iso: string, intent: CommitIntent) => {
    const suggestion = defaultScheduleOf(node, todayIso());
    if (suggestion === null || iso === '') {
      commitField(node, 'start', iso, intent);
      return;
    }
    if (iso === suggestion.startDate) {
      commitField(node, 'start', '', intent);
      return;
    }
    dispatch({ type: 'SET_NODE_DATES', updates: [{ id: node.id, endDate: suggestion.endDate }] });
    commitField(node, 'start', iso, intent);
  };
  const { setView } = usePlanView();
  /*
    A one-shot pointer at the timeline, shown under the open calendar until it
    has been followed once. Dates are really SET in the timeline — an
    unscheduled task's whole track is a click target — and nothing in the list
    said so.

    `usePersistedState` keeps its value in each component's own state, so two
    mounted rows would not see each other's write. That cannot bite: following
    the link switches the view, which unmounts every row, and they re-read the
    flag when the list comes back.
  */
  const [timelineHintSeen, setTimelineHintSeen] = usePersistedState(
    'pm.planning.timelineHintSeen',
    false,
    isBoolean,
  );
  // State, not a ref: the popover anchor must be readable during render.
  const [rowEl, setRowEl] = useState<HTMLElement | null>(null);
  /*
    Which control opened the Resources editor, so it appears beside that
    control rather than always at the row's bottom-right. `OPEN_RESOURCES`
    stays reducer state (one editor open at a time); the anchor is DOM and
    belongs to the row.
  */
  const [resources, setResources] = useState<{
    anchorEl: HTMLElement;
    align: 'left' | 'right';
  } | null>(null);
  const node = row.node;
  const numbers = rollup.nodes[node.id];
  const isActive = state.activeId === node.id;
  const isSelected = state.selectedIds.has(node.id);
  /*
    One predicate, shared with the timeline's allocation stack. It reads `kind`
    only to honour BR-2 — never to mark a *parent*: the folder icon and the
    title weight key off has-children.
  */
  const canEditResources = areResourcesEditable(node);

  const onSelectClick = (event: MouseEvent) => {
    event.stopPropagation();
    if (event.shiftKey && state.selectionAnchorId && state.selectionAnchorId !== node.id) {
      const anchorIndex = flat.index.rowIndexById.get(state.selectionAnchorId);
      const ownIndex = flat.index.rowIndexById.get(node.id);
      if (anchorIndex !== undefined && ownIndex !== undefined) {
        const [low, high] =
          anchorIndex < ownIndex ? [anchorIndex, ownIndex] : [ownIndex, anchorIndex];
        dispatch({
          type: 'SELECT_RANGE',
          ids: flat.rows.slice(low, high + 1).map((sibling) => sibling.id),
          anchorId: node.id,
        });
        return;
      }
    }
    dispatch({ type: 'TOGGLE_SELECT', id: node.id });
  };
  // CALC-22, off the rollup: resolved lines only, so a draft line nobody is on
  // does not inflate the figure the row reports as committed.
  const allocatedHours = numbers?.allocatedHours ?? 0;
  /*
    Not `canEditResources`: hours and resources are gated by different rules.
    A top-level row carries no allocations (BR-2) but does own its own hours
    once it has no children (ADR 0028) — and the Tab cycle reads the same
    predicate, so the cell and the keyboard path can no longer disagree.
  */
  const hoursEditable = isHoursEditable(node);
  const openResources = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    // 'left': the resources cell sits at the row's left, so the editor opens
    // rightwards from it instead of being clamped back off the edge.
    setResources({ anchorEl: event.currentTarget, align: 'left' });
    dispatch({ type: 'OPEN_RESOURCES', id: node.id });
  };

  return (
    <Box
      ref={setRowEl}
      data-automation-id="plan-row"
      data-node-id={node.id}
      data-depth={row.depth}
      data-drop-into={dropInto ? 'true' : undefined}
      sx={{
        position: 'relative',
        borderBottom: 'var(--border-width-thin) solid var(--border-divider)',
        bgcolor: isSelected || dragging || isActive ? 'var(--color-blue-10)' : 'transparent',
        // "The drop goes in here": the row itself is the target, which an
        // indented line under it cannot say on its own.
        ...(dropInto
          ? { outline: 'var(--focus-ring)', outlineOffset: -2, bgcolor: 'var(--color-blue-10)' }
          : {}),
        boxShadow: isActive ? 'inset 3px 0 0 var(--color-blue-80)' : 'none',
        transition: 'background var(--duration-instant)',
        '&:hover .row-hover-reveal': { opacity: 0.85 },
      }}
    >
      <Box
        onClick={(event) => {
          const target = event.target as HTMLElement;
          if (target.closest('button,input,a,select')) return;
          if (state.selectedIds.size > 0 || event.metaKey || event.ctrlKey || event.shiftKey) {
            onSelectClick(event);
            return;
          }
          dispatch({ type: 'SET_ACTIVE', id: node.id });
        }}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 2,
          py: 1,
          cursor: 'default',
        }}
      >
        <RowIconButton
          icon={isSelected ? 'check_box' : 'check_box_outline_blank'}
          title={isSelected ? t('row.DeselectRow') : t('row.SelectRow')}
          width={20}
          automationId="plan-row-checkbox"
          iconColor={isSelected ? 'var(--color-blue-80)' : undefined}
          onClick={onSelectClick}
        />

        <Box component="span" sx={{ width: row.depth * INDENT_PX, flex: 'none' }} />

        {row.hasChildren ? (
          <RowIconButton
            icon={row.folded ? 'chevron_right' : 'expand_more'}
            title={t('row.ToggleSubtasks')}
            width={18}
            iconSize={20}
            automationId="plan-row-fold"
            onClick={(event) => {
              event.stopPropagation();
              dispatch({ type: 'TOGGLE_FOLD', id: node.id });
            }}
          />
        ) : (
          <Box component="span" sx={{ width: 18, flex: 'none' }} />
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/*
            `minWidth: 0` is load-bearing: without it this flex row keeps a
            min-content floor, nothing below it can shrink, and the nowrap task
            name overflows into the Hours cell — which then paints on top of the
            title and swallows clicks on it. Any widening of the right-hand
            columns hits that wall.
          */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {row.hasChildren ? (
              <MaterialSymbol
                name="folder"
                size={15}
                color="var(--color-pink-80)"
                automationId="plan-row-parent-icon"
              />
            ) : null}
            <InlineTextCell
              value={node.name}
              display={
                <Box
                  component="span"
                  sx={{
                    fontSize: 14,
                    fontWeight: row.hasChildren ? 600 : 500,
                    color: 'var(--fg-1)',
                    // Still nowrap — a wrapping name would change row heights,
                    // and the timeline's drag maths reads those as fixed. It
                    // ellipsizes instead of overflowing.
                    whiteSpace: 'nowrap',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {node.name}
                </Box>
              }
              editing={isEditing(node.id, 'title')}
              onStartEdit={() => startEditing(node.id, 'title')}
              onCommit={(text, intent) => commitField(node, 'title', text, intent)}
              onCancel={stopEditing}
              displaySx={{ minWidth: 0, overflow: 'hidden' }}
              inputSx={{ fontWeight: row.hasChildren ? 600 : 500, flex: 1 }}
              inputWidth="60%"
              automationId="plan-row-title"
            />
            <Box
              component="span"
              sx={{
                fontFamily: 'var(--font-family-mono)',
                fontSize: 10,
                color: 'var(--fg-3)',
                flex: 'none',
              }}
            >
              {displayPlanCode(node.code)}
            </Box>
            {/*
              `flex: 'none'` and a fixed footprint, so the ellipsizing name
              absorbs it: no column width changes and the `minWidth: 0` wall
              above is untouched.
            */}
            <RowContractChip node={node} />
            {row.folded ? (
              <Box
                component="span"
                title={t('row.HiddenSubtasks')}
                data-automation-id="plan-row-hidden-count"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  flex: 'none',
                  px: 1,
                  py: 0.5,
                  borderRadius: 'var(--border-radius-pill)',
                  bgcolor: 'var(--color-fog-10)',
                  color: 'var(--color-fog-100)',
                  fontSize: 10,
                  fontWeight: 500,
                }}
              >
                <MaterialSymbol name="account_tree" size={12} />
                {row.hiddenCount}
              </Box>
            ) : null}
          </Box>
          <RowResourcesCell node={node} canEdit={canEditResources} onOpen={openResources} />
        </Box>

        <Box
          sx={{ width: COLUMN_WIDTH.hours, flex: 'none', textAlign: 'right', alignSelf: 'center' }}
        >
          {hoursEditable ? (
            <InlineTextCell
              value={node.estimatedHours !== undefined ? String(node.estimatedHours) : ''}
              display={
                <Box
                  component="span"
                  data-automation-id="plan-row-hours"
                  sx={{
                    fontFamily: 'var(--font-family-mono)',
                    fontSize: 14,
                    fontWeight: 500,
                    color: (numbers?.hours ?? 0) > 0 ? 'var(--fg-1)' : 'var(--fg-3)',
                  }}
                >
                  {formatHours(numbers?.hours ?? 0)} {t('pmCommon:unit.HoursShort')}
                </Box>
              }
              editing={isEditing(node.id, 'hours')}
              onStartEdit={() => startEditing(node.id, 'hours')}
              onCommit={(text, intent) => commitField(node, 'hours', text, intent)}
              onCancel={stopEditing}
              align="right"
              underlineColor="var(--color-pink-80)"
              inputWidth={52}
              inputSx={{ fontFamily: 'var(--font-family-mono)', fontWeight: 500 }}
              automationId="plan-row-hours-cell"
            />
          ) : (
            <Box
              component="span"
              data-automation-id="plan-row-hours"
              // The only thing that takes the hours away is children (BR-1).
              title={t('row.HoursRollUp')}
              sx={{
                fontFamily: 'var(--font-family-mono)',
                fontSize: 14,
                // Bold like the parent's name (2026-10-08, the pilot's ask): a
                // rolled-up sum reads apart from the hours typed on a leaf.
                fontWeight: 600,
                color: (numbers?.hours ?? 0) > 0 ? 'var(--fg-1)' : 'var(--fg-3)',
              }}
            >
              {formatHours(numbers?.hours ?? 0)} {t('pmCommon:unit.HoursShort')}
            </Box>
          )}
          <Box
            sx={{
              fontFamily: 'var(--font-family-mono)',
              fontSize: 10,
              color: 'var(--fg-3)',
              mt: 0.5,
            }}
          >
            {allocatedHours > 0
              ? t('projectManagement:projectPlan.Columns.HoursAllocated', {
                  hours: formatHours(allocatedHours),
                })
              : '—'}
          </Box>
        </Box>

        {/*
          The start date (Mette, 3 Nov), where the Status column was — a draft
          has no status to set, but its schedule matters.
        */}
        <Box
          sx={{
            width: COLUMN_WIDTH.startDate,
            flex: 'none',
            textAlign: 'right',
            alignSelf: 'center',
          }}
        >
          <InlineDateCell
            value={node.startDate ?? ''}
            display={
              <Box
                component="span"
                data-automation-id="plan-row-start"
                sx={{ fontSize: 14, fontWeight: 500, color: 'var(--fg-1)' }}
              >
                {formatShortDate(node.startDate)}
              </Box>
            }
            editing={isEditing(node.id, 'start')}
            autoOpen={shouldOpenPicker(node.id, 'start')}
            onStartEdit={() => startEditing(node.id, 'start', true)}
            onCommit={commitStart}
            onCancel={stopEditing}
            align="right"
            automationId="plan-row-start-cell"
          />
        </Box>

        <Box
          sx={{
            width: COLUMN_WIDTH.targetDate,
            flex: 'none',
            textAlign: 'right',
            alignSelf: 'center',
          }}
        >
          <InlineDateCell
            value={node.targetDate ?? ''}
            display={
              <Box
                component="span"
                data-automation-id="plan-row-target"
                sx={{ fontSize: 14, fontWeight: 500, color: 'var(--fg-1)' }}
              >
                {formatShortDate(node.targetDate)}
              </Box>
            }
            editing={isEditing(node.id, 'target')}
            autoOpen={shouldOpenPicker(node.id, 'target')}
            onStartEdit={() => startEditing(node.id, 'target', true)}
            onCommit={(iso, intent) => commitField(node, 'target', iso, intent)}
            onCancel={stopEditing}
            align="right"
            automationId="plan-row-target-cell"
            panelAction={
              timelineHintSeen
                ? undefined
                : {
                    label: t('row.SetDatesInTimeline'),
                    icon: 'view_timeline',
                    automationId: 'plan-row-target-cell-timeline-link',
                    /*
                      Written from the handler, never from render: the keyboard
                      hints' seen-counter was written inside a `useMemo`, which
                      StrictMode double-invokes, and it burned through its
                      allowance in under two tasks in dev.

                      No SET_ACTIVE needed — START_EDITING already made this row
                      active, so the timeline's mount effect scrolls to it and
                      tints it.
                    */
                    onSelect: () => {
                      setTimelineHintSeen(true);
                      setView('timeline');
                    },
                  }
            }
          />
          <Box sx={{ mt: 0.5 }}>
            <DaysLeftLabel targetDate={node.targetDate} />
          </Box>
        </Box>

        <Box
          sx={{
            width: COLUMN_WIDTH.trailing,
            flex: 'none',
            alignSelf: 'center',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <RowKebab
            row={row}
            canEditResources={canEditResources}
            onOpenResources={(element) => setResources({ anchorEl: element, align: 'right' })}
          />
          <Box
            component="span"
            className="row-hover-reveal"
            data-automation-id="plan-row-drag-handle"
            onPointerDown={onDragHandleDown}
            sx={{
              width: 24,
              height: 24,
              flex: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--fg-3)',
              opacity: 0,
              transition: 'opacity var(--duration-instant)',
              cursor: 'grab',
              touchAction: 'none',
              '&:active': { cursor: 'grabbing' },
            }}
          >
            <MaterialSymbol name="drag_handle" size={18} />
          </Box>
        </Box>
      </Box>

      {isEditing(node.id, 'title') || isEditing(node.id, 'hours') ? <KeyboardHintBar /> : null}

      {state.resourcesNodeId === node.id ? (
        <ResourcesPopover
          node={node}
          anchorEl={resources?.anchorEl ?? rowEl}
          align={resources?.align ?? 'right'}
        />
      ) : null}
    </Box>
  );
};
